import React from 'react';
import { ArrowUpDown, CheckSquare, Square, Trash2, Minus } from 'lucide-react';
import { ImportFileInfo } from '../../../../core/types/import.types';
import { ImportFileItem } from './ImportFileItem';
import { cn } from '../../../utils/cn';

interface Props {
  files: ImportFileInfo[];
  selectedFileId: string | null;
  sortBy: 'name' | 'type' | 'size';
  sortOrder: 'asc' | 'desc';
  onSelectFile: (fileId: string | null) => void;
  onToggleChecked: (fileId: string) => void;
  onRemoveFile: (fileId: string) => void;
  onConfigureFile: (fileId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onClearAll: () => void;
  onSortChange: (sortBy: 'name' | 'type' | 'size') => void;
  onSortOrderToggle: () => void;
}

export const ImportFileList: React.FC<Props> = ({
  files,
  selectedFileId,
  sortBy,
  sortOrder,
  onSelectFile,
  onToggleChecked,
  onRemoveFile,
  onConfigureFile,
  onSelectAll,
  onDeselectAll,
  onClearAll,
  onSortChange,
  onSortOrderToggle,
}) => {
  const allSelected = files.length > 0 && files.every(f => f.isSelected);
  const someSelected = files.some(f => f.isSelected);
  const noneSelected = !someSelected;

  const selectedCount = files.filter(f => f.isSelected).length;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700 bg-zinc-800/50">
        <div className="flex items-center gap-3">
          {/* Select all checkbox */}
          <button
            onClick={allSelected ? onDeselectAll : onSelectAll}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            {allSelected ? (
              <CheckSquare size={16} className="text-blue-400" />
            ) : someSelected ? (
              <div className="relative">
                <Square size={16} className="text-zinc-500" />
                <Minus size={10} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-blue-400" />
              </div>
            ) : (
              <Square size={16} />
            )}
            <span>
              {selectedCount} of {files.length} selected
            </span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* Sort dropdown */}
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value as 'name' | 'type' | 'size')}
            className="px-2 py-1 text-xs bg-zinc-700 border border-zinc-600 rounded text-zinc-300 focus:outline-none focus:border-blue-500 cursor-pointer"
          >
            <option value="name">Sort by Name</option>
            <option value="type">Sort by Type</option>
            <option value="size">Sort by Size</option>
          </select>

          {/* Sort order toggle */}
          <button
            onClick={onSortOrderToggle}
            className={cn(
              'p-1.5 rounded hover:bg-zinc-700 transition-all',
              sortOrder === 'desc' && 'rotate-180'
            )}
            title={`Sort ${sortOrder === 'asc' ? 'ascending' : 'descending'}`}
          >
            <ArrowUpDown size={14} className="text-zinc-400" />
          </button>

          {/* Clear all button */}
          {files.length > 0 && (
            <button
              onClick={onClearAll}
              className="flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-600/20 rounded transition-colors"
            >
              <Trash2 size={12} />
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* File list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {files.length === 0 ? (
          <div className="flex items-center justify-center h-full text-zinc-500 text-sm">
            No files added yet
          </div>
        ) : (
          files.map((file) => (
            <ImportFileItem
              key={file.id}
              file={file}
              isSelected={file.id === selectedFileId}
              onSelect={() => onSelectFile(file.id)}
              onToggleChecked={() => onToggleChecked(file.id)}
              onRemove={() => onRemoveFile(file.id)}
              onConfigure={() => onConfigureFile(file.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};
